import {CampaignStep} from 'common/src/modules/campaignWorkflow/campaignWorkflow.enums';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {campaignTypeConfigs} from '../../campaigns.config';
import {CampaignSubTypeName, CampaignTypeName} from '../../campaigns.types';
import fetchCampaigns from '../../store/campaignsLibrary/thunks/fetchCampaigns';
import addCampaign from '../../utils/addCampaign';
import navigateToCampaignVersion from '../../utils/navigateToCampaignVersion';
import {CampaignToolName} from '../copilotCampaigns.enums';

type CampaignOptionInfo = {
  type: CampaignTypeName;
  subtype: CampaignSubTypeName | null;
};

function createCampaignTool({
  isAllowingSocialCampaigns = true,
  store,
}: {
  isAllowingSocialCampaigns?: boolean;
  store: CLStore;
}) {
  const availableCampaignOptions = _getAvailableCampaignOptions(isAllowingSocialCampaigns);
  const inputSchema = _buildInputSchema(Object.keys(availableCampaignOptions));

  async function execute({
    input,
    toolUtils: {askUserConfirmation},
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    // initialize result object
    // Note: Tool returns validation issues without succeeding, allowing deterministic error handling
    // instead of relying on LLM instructions for validation logic
    const result = {
      issues: [] as string[],
      didSucceed: false,
    };

    // __ handle campaign name: __
    if (!input.campaignName) {
      result.issues.push('Campaign name is required');
    }

    if (!input.campaignTypeOption) {
      const optionsList = Object.keys(availableCampaignOptions)
        .map((option, index) => `${index + 1}. ${option}`)
        .join(', ');
      result.issues.push(`Campaign type is required. Available options: ${optionsList}`);
    }

    const {campaignTypeOption} = input;

    // quick exit if there are ANY issue
    if (result.issues.length > 0 || !campaignTypeOption) {
      return toolResult(JSON.stringify(result));
    }

    const campaignOption = availableCampaignOptions[campaignTypeOption];

    if (!campaignOption) {
      result.issues.push(`Invalid campaign type option: ${campaignTypeOption}`);

      return toolResult(JSON.stringify(result));
    }

    const {type: campaignType, subtype: campaignSubType} = campaignOption;

    const campaignId = await addCampaign({
      campaignName: input.campaignName ?? undefined,
      campaignType,
      campaignSubType: campaignSubType ?? undefined,
    });

    // Refresh campaigns list after creating a new campaign
    store.dispatch(fetchCampaigns());

    // Ask user if they want to navigate to the new campaign
    const shouldNavigate = await askUserConfirmation({
      title: 'Campaign Created',
      text: `Campaign "${input.campaignName}" has been created successfully. Would you like to navigate to the campaign?`,
      okLabel: 'Go to Campaign',
      cancelLabel: 'Stay Here',
    });

    if (shouldNavigate) {
      await navigateToCampaignVersion({
        getState: store.getState,
        steps: CampaignStep.CampaignLogic,
        campaignId,
      });

      return toolResult(
        `A new campaign has been created with ID=${campaignId}` +
          `${input.campaignName ? `, name='${input.campaignName}'` : ''}` +
          `, type='${campaignTypeOption}'. User accepted navigation to the new campaign's page and is now at the campaign page.`,
      );
    }

    return toolResult(
      `A new campaign has been created with ID=${campaignId}` +
        `${input.campaignName ? `, name='${input.campaignName}'` : ''}` +
        `, type='${campaignTypeOption}'. User declined navigation to the new campaign's page and decided to stay at their current location.`,
    );
  }

  return tool({
    name: CampaignToolName.CreateCampaign,
    displayName: 'Creating campaign',
    description: `
    Creates a new campaign in the system.

    ## When to Call
    Call this tool whenever the user wants to create a campaign, EVEN IF you don't have 
    all the required details. Do NOT wait to gather all information before calling—
    the tool is designed for iterative use and will tell you exactly what's missing.

    ## Important
    - DO NOT ASSUME or guess any field. the user needs to explicitly specify the values for each field.
    - The issues are context-aware and reflect real-time application state
    `.trim(),
    execute,
    inputSchema,
  });
}

function _buildInputSchema(availableCampaignOptions: string[]) {
  return z.object({
    campaignName: z.string().optional().nullable().describe('The name of the campaign to create'),
    campaignTypeOption: z
      .enum(availableCampaignOptions as [string, ...string[]])
      .optional()
      .nullable()
      .describe('The type of campaign to create (includes subtype where applicable)'),
  });
}

/**
 * Generates a map of available campaign options.
 * Keys are display labels (e.g., "Display", "Facebook Standard Campaign").
 * Values contain the corresponding type and subtype for campaign creation.
 */
function _getAvailableCampaignOptions(
  isAllowingSocialCampaigns: boolean,
): Record<string, CampaignOptionInfo> {
  const campaignOptions: Record<string, CampaignOptionInfo> = {};

  for (const config of campaignTypeConfigs) {
    if (!config.testIsEnabled()) {
      continue;
    }

    if (config.isSocial && !isAllowingSocialCampaigns) {
      continue;
    }

    if (config.subtypes.length === 0) {
      campaignOptions[config.label] = {
        type: config.type,
        subtype: null,
      };

      continue;
    }

    const enabledSubtypes = config.subtypes.filter((subtype) => subtype.testIsEnabled());

    for (const subtype of enabledSubtypes) {
      campaignOptions[`${config.label} ${subtype.label}`] = {
        type: config.type,
        subtype: subtype.type,
      };
    }
  }

  return campaignOptions;
}

export default createCampaignTool;
